// Pure helpers for the clip recorder (client/clip.ts), kept free of the DOM so
// bun test can cover them (apps/web/test/clip.test.ts).

/** MediaRecorder types in order of preference: MP4 (H.264) where the browser records it, else WebM. */
export const MIME_WITH_AUDIO = [
	'video/mp4;codecs="avc1.640028,mp4a.40.2"',
	'video/mp4;codecs="avc1.4d002a,mp4a.40.2"',
	'video/mp4;codecs="avc1.42E01E,mp4a.40.2"',
	"video/mp4;codecs=avc1,mp4a",
	"video/mp4",
	"video/webm;codecs=vp9,opus",
	"video/webm;codecs=vp8,opus",
	"video/webm",
];
export const MIME_VIDEO_ONLY = [
	'video/mp4;codecs="avc1.640028"',
	'video/mp4;codecs="avc1.4d002a"',
	'video/mp4;codecs="avc1.42E01E"',
	"video/mp4;codecs=avc1",
	"video/mp4",
	"video/webm;codecs=vp9",
	"video/webm;codecs=vp8",
	"video/webm",
];

/** The first type `isSupported` accepts; null when none (record with the browser's default). */
export function pickMime(
	isSupported: (type: string) => boolean,
	withAudio: boolean,
): string | null {
	for (const t of withAudio ? MIME_WITH_AUDIO : MIME_VIDEO_ONLY) {
		try {
			if (isSupported(t)) return t;
		} catch {}
	}
	return null;
}

/** "video/mp4;codecs=…" → "video/mp4" (the Blob's and File's type). */
export function baseMime(mime: string | null | undefined): string {
	const base = (mime ?? "").split(";")[0].trim().toLowerCase();
	return base || "video/webm";
}

export function extensionFor(mime: string): "mp4" | "webm" {
	return baseMime(mime) === "video/mp4" ? "mp4" : "webm";
}

/** A safe file name with the extension that matches what was recorded. */
export function clipFilename(
	requested: string | null | undefined,
	mime: string,
): string {
	const ext = extensionFor(mime);
	let stem = (requested ?? "")
		.trim()
		.replace(/\.(mp4|webm|mov|m4v|mkv)$/i, "")
		.replace(/[^A-Za-z0-9._ -]+/g, "-")
		.replace(/^[.\s-]+|[.\s-]+$/g, "")
		.slice(0, 80);
	if (!stem) stem = "heavensteel-clip";
	return `${stem}.${ext}`;
}

export function formatBytes(n: number): string {
	if (!Number.isFinite(n) || n < 0) return "";
	if (n < 1024) return `${n} B`;
	if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
	return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

export function formatDuration(seconds: number): string {
	if (!Number.isFinite(seconds) || seconds <= 0) return "";
	const s = Math.round(seconds);
	return s < 60
		? `${s} s`
		: `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
}

/** The replay page for a server-rendered clip URL (/r/CODE/clip.mp4 → /r/CODE). */
export function pageForClipUrl(url: string): string {
	return url.replace(/\/(clip\.mp4|og\.jpg)(\?.*)?$/, "");
}

/** fps for captureStream: 1–60, default 30. */
export function clampFps(fps: unknown): number {
	const n = Number(fps);
	return Number.isFinite(n) && n >= 1 ? Math.min(60, Math.round(n)) : 30;
}
